import { Box, Button, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DragEvent, KeyboardEvent, useRef } from 'react';
import IconifyLegacy from 'src/components/iconify/iconify-legacy';
import { BENEFITS_URL } from 'src/config-global';

type DaysModalProps = {
  open: boolean;
  fileName: string;
  scheduleType: 'hour' | 'day' | 'simple' | 'external';
  isEdit: boolean;
  eventId: string;
  onClose: VoidFunction;
  handleUpload: (file: File) => void;
};

export default function DaysModal({
  open,
  fileName,
  scheduleType,
  isEdit,
  eventId,
  onClose,
  handleUpload,
}: DaysModalProps) {
  const inputFile = useRef<HTMLInputElement | null>(null);

  const isHourlySchedule = scheduleType === 'hour';
  const spreadsheetFormat = isHourlySchedule
    ? 'DD/MM/AAAA  |  HH:mm  |  Vagas'
    : 'DD/MM/AAAA  |  Vagas';
  const spreadsheetExample = isHourlySchedule
    ? {
        headers: ['Dia', 'Hora', 'Vagas'],
        values: ['18/08/2026', '09:30', '40'],
        description: 'Há um total de 40 vagas para o horário de 18/08/2026 às 09:30.',
      }
    : {
        headers: ['Dia', 'Vagas'],
        values: ['18/08/2026', '40'],
        description: 'Há um total de 40 vagas disponíveis para o dia 18/08/2026.',
      };

  const getHref = () => {
    if (!isEdit) {
      return scheduleType === 'day' ? '/planilha_day.xlsx' : '/planilha_hour.xlsx';
    }

    return `${BENEFITS_URL}/benefits/events/event-slot-xlsx/${eventId}/${scheduleType}`;
  };

  const selectFile = (file?: File) => {
    if (file?.name.toLowerCase().endsWith('.xlsx')) {
      handleUpload(file);
    }
  };

  const openFilePicker = () => {
    if (inputFile.current) {
      inputFile.current.value = '';
      inputFile.current.click();
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    selectFile(event.dataTransfer.files?.[0]);
  };

  const handleUploadKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Stack spacing={3} sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Registrar horários do evento</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Preencha o modelo e envie o arquivo para cadastrar os horários em massa.
            </Typography>
          </Box>

          <IconButton aria-label="Fechar" onClick={onClose} sx={{ mt: -1, mr: -1 }}>
            <IconifyLegacy icon="mdi:close" width={22} />
          </IconButton>
        </Stack>

        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'common.white',
                typography: 'subtitle2',
              }}
            >
              1
            </Box>

            <Stack spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
              <Box>
                <Typography variant="subtitle1">
                  {isEdit ? 'Baixe a planilha atual' : 'Baixe e preencha o modelo'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isEdit
                    ? 'Ajuste os horários sem alterar os cabeçalhos da planilha.'
                    : 'Mantenha os cabeçalhos e substitua a linha de exemplo pelos dados do evento.'}
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  color: 'text.secondary',
                  typography: 'caption',
                  width: 'fit-content',
                }}
              >
                Formato:{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {spreadsheetFormat}
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 600 }}
                >
                  Exemplo de uso
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isHourlySchedule
                      ? 'minmax(110px, 1.4fr) minmax(75px, 1fr) minmax(65px, 0.8fr)'
                      : 'minmax(110px, 1.4fr) minmax(65px, 0.8fr)',
                    maxWidth: isHourlySchedule ? 360 : 250,
                    borderTop: '1px solid',
                    borderLeft: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  {spreadsheetExample.headers.map((header) => (
                    <Box
                      key={header}
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderRight: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: 'grey.300',
                        bgcolor: 'grey.200',
                        color: 'grey.800',
                        typography: 'caption',
                        fontWeight: 700,
                      }}
                    >
                      {header}
                    </Box>
                  ))}

                  {spreadsheetExample.values.map((value, index) => (
                    <Box
                      key={spreadsheetExample.headers[index]}
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderRight: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: 'grey.300',
                        bgcolor: 'common.white',
                        color: 'grey.800',
                        typography: 'caption',
                      }}
                    >
                      {value}
                    </Box>
                  ))}
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.75 }}
                >
                  {spreadsheetExample.description}
                </Typography>
              </Box>

              <Button
                component="a"
                href={getHref()}
                download
                variant="outlined"
                startIcon={<IconifyLegacy icon="mdi:microsoft-excel" width={20} />}
                sx={{ alignSelf: 'flex-start' }}
              >
                {isEdit ? 'Baixar planilha atual' : 'Baixar planilha modelo'}
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'common.white',
                typography: 'subtitle2',
              }}
            >
              2
            </Box>

            <Stack spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
              <Box>
                <Typography variant="subtitle1">Envie a planilha preenchida</Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecione ou arraste para cá um único arquivo no formato .xlsx.
                </Typography>
              </Box>

              <Box
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={handleUploadKeyDown}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                sx={{
                  minHeight: 132,
                  px: 2,
                  py: 2.5,
                  border: '1px dashed',
                  borderColor: fileName ? 'success.main' : 'grey.400',
                  borderRadius: 1.5,
                  bgcolor: (theme) =>
                    fileName
                      ? alpha(theme.palette.success.main, 0.06)
                      : alpha(theme.palette.grey[500], 0.04),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: (theme) =>
                    theme.transitions.create(['border-color', 'background-color']),
                  '&:hover, &:focus-visible': {
                    borderColor: fileName ? 'success.main' : 'primary.main',
                    bgcolor: (theme) =>
                      fileName
                        ? alpha(theme.palette.success.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.06),
                    outline: 'none',
                  },
                }}
              >
                <input
                  onChange={(event) => selectFile(event.target.files?.[0])}
                  ref={inputFile}
                  type="file"
                  accept=".xlsx"
                  hidden
                />

                <IconifyLegacy
                  icon={fileName ? 'mdi:file-check-outline' : 'mdi:cloud-upload-outline'}
                  width={36}
                  sx={{ color: fileName ? 'success.main' : 'primary.main', mb: 1 }}
                />
                <Typography variant="subtitle2" sx={{ maxWidth: '100%', wordBreak: 'break-word' }}>
                  {fileName || 'Clique para selecionar a planilha'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                  {fileName
                    ? 'Arquivo selecionado — clique para substituir'
                    : 'ou arraste o arquivo aqui'}
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Os horários são processados automaticamente após a seleção do arquivo.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
}
